/* Display only: never changes product prices, variants, cart requests or discounts. */
(() => {
  'use strict';
  if (customElements.get('pd2-early-campaign')) return;

  class EarlySlot extends HTMLElement {
    connectedCallback() {
      this.dispatchEvent(new CustomEvent('pd2:early-slot-ready', { bubbles: true }));
    }
  }

  class EarlyCampaign extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.slots = new Set();
      this.refresh = this.refresh.bind(this);
      this.observer = new ResizeObserver(() => this.updateSpacer());
      const options = { signal: this.abort.signal };
      document.addEventListener('pd2:early-slot-ready', this.refresh, options);
      document.addEventListener('shopify:section:load', this.refresh, options);
      document.addEventListener('shopify:section:unload', this.refresh, options);
      document.addEventListener('visibilitychange', this.refresh, options);
      window.addEventListener('pageshow', this.refresh, options);
      window.addEventListener('resize', this.refresh, options);
      this.frame = requestAnimationFrame(this.refresh);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.observer?.disconnect();
      this.slots?.forEach(slot => this.release(slot));
      this.spacer?.remove();
      this.spacer = null;
    }

    parseTime(value) {
      // Require an explicit offset so a visitor's timezone never changes the schedule.
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:\d{2})$/.test(value || '')) return NaN;
      const datePart = value.slice(0, 10);
      const date = new Date(datePart + 'T00:00:00Z');
      if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== datePart) return NaN;
      return Date.parse(value);
    }

    release(slot) {
      if (slot.earlyOwner !== this) return;
      slot.hidden = true;
      slot.replaceChildren();
      slot.style.removeProperty('--pd2-early-bg');
      slot.style.removeProperty('--pd2-early-fg');
      delete slot.earlyOwner;
      this.observer?.unobserve(slot);
    }

    refresh() {
      if (!this.isConnected) return;
      clearTimeout(this.timer);
      const start = this.parseTime(this.dataset.startsAt);
      const end = this.parseTime(this.dataset.endsAt);
      const now = Date.now();
      const valid = this.dataset.enabled === 'true' && Boolean(this.dataset.productId) &&
        Number.isFinite(start) && Number.isFinite(end) && end > start;
      const preview = this.dataset.editorPreview === 'true';
      const active = valid && (preview || (now >= start && now < end));
      const banner = this.querySelector('[data-early-banner]');
      if (banner) banner.hidden = !(active && this.dataset.showBanner === 'true');
      const editor = this.querySelector('[data-early-editor]');
      if (editor) {
        editor.hidden = false;
        editor.textContent = !valid
          ? '早鸟活动未显示：请启用活动、选择产品，并填写有效的起止时间。'
          : preview
            ? '编辑器预览：正在忽略时间展示。顾客页面仍严格按配置的活动时间显示。'
            : active ? '早鸟活动正在生效（按浏览器时间判断）。' : '活动时间外：顾客页面隐藏早鸟提示。';
      }

      for (const slot of this.slots) {
        if (!slot.isConnected) {
          this.release(slot);
          this.slots.delete(slot);
        }
      }
      document.querySelectorAll('pd2-early-slot[data-pd2-early-slot]').forEach(slot => {
        const type = slot.dataset.pd2EarlySlot;
        const allowed = type === 'card' ? this.dataset.showCard === 'true'
          : type === 'sticky' && this.dataset.showSticky === 'true';
        if (!active || !allowed || slot.dataset.productId !== this.dataset.productId) {
          this.release(slot);
          this.slots.delete(slot);
          return;
        }
        const template = this.querySelector(`[data-early-template="${type}"]`);
        if (!template) return;
        if (slot.earlyOwner !== this) {
          slot.replaceChildren(template.content.cloneNode(true));
          slot.earlyOwner = this;
          slot.style.setProperty('--pd2-early-bg', this.dataset.background);
          slot.style.setProperty('--pd2-early-fg', this.dataset.foreground);
          this.slots.add(slot);
          if (type === 'sticky') this.observer.observe(slot);
        }
        slot.hidden = false;
      });
      this.updateSpacer();
      // Recheck after sleeping tabs/clock changes; wake precisely at the next boundary.
      const next = now < start ? start : now < end ? end : Infinity;
      this.timer = setTimeout(this.refresh, Math.min(60000, Math.max(1, next - now)));
    }

    updateSpacer() {
      let height = 0;
      this.slots?.forEach(slot => {
        if (!slot.hidden && slot.dataset.pd2EarlySlot === 'sticky' && slot.isConnected) {
          height = Math.max(height, slot.getBoundingClientRect().height);
        }
      });
      if (height > 0) {
        if (!this.spacer) {
          this.spacer = document.createElement('div');
          this.spacer.className = 'pd2-early-footer-space';
          this.spacer.setAttribute('aria-hidden', 'true');
          document.body.append(this.spacer);
        }
        this.spacer.style.height = `${Math.ceil(height)}px`;
      } else {
        this.spacer?.remove();
        this.spacer = null;
      }
    }
  }

  if (!customElements.get('pd2-early-slot')) customElements.define('pd2-early-slot', EarlySlot);
  customElements.define('pd2-early-campaign', EarlyCampaign);
})();